<!--
  PasswordField — campo de contraseña de Otera con toggle mostrar/ocultar.
  Reemplaza a PasswordFieldLegacy. Compuesto sobre Field + Input; el toggle
  vive en el slot #suffix del Input. Preserva el foco al alternar (UX-DR7).
-->

<script setup>
import { computed, ref, useId } from 'vue';

import Field from './Field.vue';
import Icon from './Icon.vue';
import Input from './Input.vue';

defineOptions({ name: 'DsPasswordField', inheritAttrs: false });

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: 'Password' },
  id: { type: String, default: null },
  autocomplete: { type: String, default: 'current-password' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: null },
  hint: { type: String, default: null }
});

defineEmits(['update:modelValue']);

const fallbackId = useId();
const inputId = computed(() => props.id || `password-field-${fallbackId}`);
const visible = ref(false);
const inputRef = ref(null);

const inputType = computed(() => (visible.value ? 'text' : 'password'));
const toggleLabel = computed(() => (visible.value ? 'Hide password' : 'Show password'));

function toggle() {
  visible.value = !visible.value;
  inputRef.value?.focus();
}
</script>

<template>
  <Field
    :label="label"
    :required="required"
    :error="error"
    :hint="hint"
    :html-for="inputId"
  >
    <Input
      :id="inputId"
      ref="inputRef"
      :model-value="modelValue"
      :type="inputType"
      :error="!!error"
      :autocomplete="autocomplete"
      :disabled="disabled || undefined"
      :required="required || undefined"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <template #suffix>
        <button
          type="button"
          class="pw-toggle"
          :aria-label="toggleLabel"
          :aria-pressed="visible"
          :disabled="disabled"
          @click="toggle"
        >
          <Icon
            :name="visible ? 'eyeOff' : 'eye'"
            :size="14"
          />
        </button>
      </template>
    </Input>
  </Field>
</template>

<style scoped>
.pw-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--fg-tertiary);
  border-radius: var(--radius-sm);
}

.pw-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pw-toggle:hover:not(:disabled) {
  color: var(--fg-primary);
}

</style>
